/**
 * Imports
 *
 * Import any modules or components here.
 */
import {
    i18n,
    userLanguage,
    appLocale,
    appDomain,
} from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
// import { _boostrap_form_validation } from "commonAssets/lib/bootstrap_form_validation.js";
import { slugify } from "commonAssets/lib/slugify.js";

/**
 * Represents a class for generating the Important Dates section of the website.
 * @class
 * @param {Object} json - The JSON object containing the dates to be displayed.
 * @param {Array} longDate - The array of date strings that require a long format.
 */
export class ImportantDates {
    constructor(
        params,
        timeZone = appDomain.options.claimDeadlineTimezone,
        courtTimeZone = appDomain.options.courtDateTimezone
    ) {
        this.params = params;
        this.timeZone = timeZone ? timeZone : "America/Los_Angeles";
        this.courtTimeZone = courtTimeZone ? courtTimeZone : this.timeZone;
        this.dateTimeConfig = this.normalizeDateTimeConfig(
            appDomain?.options?.importantDatesUseDateTime
        );
    }

    /**
     * Generates an HTML card element with important dates.
     * @param {string} classSelector - The CSS class selector for the card element.
     * @param {string} idSelector - The ID selector for the card element.
     * @returns {string} The HTML card element as a string.
     */
    card(
        classSelector = "card bg-light text-dark border rounded p-3 impt-dates",
        idSelector = "importantDatesCard"
    ) {
        const card = `
        <div class="${classSelector}" id="${idSelector}">
            <div class="h3 text-danger text-center mb-4 card-title" id="impt-dates-title">
                ${i18n.getTranslation("important_dates")}
            </div>
            <div class="card-body p-0" id="impt-dates-body">
            ${this.datesList()}
            </div>
        </div>
        `;
        return card;
    }

    /**
     * Generates an HTML unordered list of important dates.
     * @returns {string} The HTML string of the unordered list.
     */
    datesList() {
        const json = this.params.findResult;

        const cardList = createElement("ul");
        cardList.classList.add("list-group", "list-group-flush"); // Add classes to the element
        cardList.id = "impt-dates-body"; // Add an ID to the element

        const titles = json.lang[userLanguage].ImportantDates;
        const dates = json.ImportantDates;
        let dateID = 0;
        dates.forEach((li) => {
            const listItem = createElement("li");
            listItem.classList.add("list-group-item", "important-date"); // Add classes to the element
            listItem.id = slugify(titles[dateID]?.Title); // Add an ID to the element

            const title = titles[dateID]?.Title;
            const display_date = (title) => {
                    return this.dateString(li.Date);
            };
            const body = `<time class="d-block fw-bold">${display_date(title)}</time><span>${title}</span>`;
            listItem.innerHTML = body;

            cardList.appendChild(listItem);
            dateID++;
        });
        return cardList.outerHTML;
    }

    /**
     * Returns a formatted date string based on the given parameters.
     *
     * @param {string} params - The date to be formatted.
     * @returns {string} - The formatted date string.
     */
    dateString(params) {
        const date = new Date(params).toLocaleString(appLocale, {
            timeZone: this.timeZone,
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        return date;
    }

    // Defaults to PST/PDT
    /**
     * Returns a formatted date and time string in the America/Los_Angeles timezone.
     * @param {string} params - The date and time string to format.
     * @returns {string} The formatted date and time string.
     */
    dateTime(params) {
        const date = new Date(params).toLocaleString(appLocale, {
            timeZone: this.courtTimeZone,
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        });
        return date;
    }

    normalizeDateTimeConfig(config) {
        if (!config) {
            return new Set();
        }

        const normalized = Array.isArray(config) ? config : [config];

        return new Set(
            normalized
                .map((item) => {
                    if (!item && item !== 0) {
                        return null;
                    }
                    if (typeof item === "object") {
                        const entries = ["title", "type", "slug"]
                            .map((key) =>
                                item[key]
                                    ? item[key].toString().trim().toLowerCase()
                                    : null
                            )
                            .filter(Boolean);
                        return entries.length ? entries : null;
                    }
                    return item.toString().trim().toLowerCase();
                })
                .filter(Boolean)
                .flat()
        );
    }

    shouldDisplayAsDateTime(dateItem, title = "") {
        if (!dateItem && !title) {
            return false;
        }

        const configMatch = () => {
            const candidates = [
                title?.toString().trim().toLowerCase(),
                dateItem?.Type?.toString().trim().toLowerCase(),
                slugify(title || "").toLowerCase(),
            ].filter(Boolean);

            return candidates.some((candidate) =>
                this.dateTimeConfig.has(candidate)
            );
        };

        if (configMatch()) {
            return true;
        }

        const longDateTranslation =
            i18n.getTranslation("long_date")?.toLowerCase() || "";

        return Boolean(
            longDateTranslation &&
            title?.toString().toLowerCase().includes(longDateTranslation)
        );
    }
}
